---
id: iam-policy-review
title: IAM Policy Least Privilege Review
category: security
difficulty: advanced
tags:
  - iam
  - security
  - aws
  - azure
  - least-privilege
  - permissions
description: >
  Reviews IAM policies for over-permissive access and generates
  least-privilege alternatives.
whenToUse: >
  When auditing IAM policies, onboarding new services, or responding
  to security findings about excessive permissions.
variables:
  - name: iam_policy
    desc: The IAM policy JSON to review
  - name: cloud
    desc: Cloud provider (AWS, Azure, GCP)
  - name: principal
    desc: What this policy is attached to
  - name: purpose
    desc: What this principal is supposed to do
  - name: access_logs
    desc: CloudTrail/Activity Log showing actual API calls (last 30 days)
tips:
  - Always include access logs when available — they show what permissions are actually needed.
  - Review policies attached to service accounts first — they often have more access than needed.
  - Check for iam:PassRole, sts:AssumeRole, and * actions — these are privilege escalation vectors.
---

You are an IAM security specialist. Review this policy for least-privilege compliance.

## Current Policy
```json
{{iam_policy}}
```

## Context
- Cloud: {{cloud}}
- Attached to: {{principal}} (role, user, service account)
- Purpose: {{purpose}}
- Actual API calls made (from access logs):
```
{{access_logs}}
```

## Task
1. Identify over-permissive statements (wildcards, broad resources, unnecessary actions)
2. Cross-reference with actual usage from access logs
3. Generate a least-privilege replacement policy
4. Flag any dangerous permissions (iam:*, s3:*, admin access)

## Output Format (JSON)
{
  "risk_score": "critical | high | medium | low",
  "over_permissive": [
    {
      "statement": "which statement",
      "issue": "what's over-permissive",
      "actual_usage": "what's actually used",
      "recommendation": "how to scope down"
    }
  ],
  "dangerous_permissions": ["list of dangerous actions found"],
  "recommended_policy": { "... least privilege policy JSON ..." },
  "unused_permissions": ["actions granted but never used"]
}

<!-- EXAMPLE_OUTPUT -->

{
  "risk_score": "high",
  "over_permissive": [
    {
      "statement": "Statement 1 — Allow s3:*",
      "issue": "Grants all S3 actions on all buckets",
      "actual_usage": "Only s3:GetObject and s3:PutObject on bucket 'app-assets'",
      "recommendation": "Scope to specific actions and bucket ARN"
    }
  ],
  "dangerous_permissions": ["s3:*", "iam:PassRole"],
  "recommended_policy": { "Version": "2012-10-17", "Statement": ["..."] }
}
